import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, DownloadIcon, AlertCircleIcon, EyeIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Helper to generate consistent random data based on ID
const generateRandomData = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const cardTypes = ['VISA', 'MASTERCARD', 'UNIONPAY', 'PAYPAK'];
  const cardType = cardTypes[hash % 4];
  let cardPrefix = '4';
  if (cardType === 'MASTERCARD') cardPrefix = '5';
  if (cardType === 'UNIONPAY') cardPrefix = '6';
  if (cardType === 'PAYPAK') cardPrefix = '2';
  const cities = ['Karachi', 'Lahore', 'Islamabad', 'Peshawar', 'Faisalabad', 'Multan', 'Rawalpindi', 'Sialkot'];
  const city = cities[hash % cities.length];
  return {
    cardType,
    cardPrefix,
    city,
    terminalId: `TID-${10000000 + hash * 12345 % 90000000}`,
    authCode: `${100000 + hash * 54321 % 900000}`,
    rrn: `${100000000000 + hash * 98765 % 900000000000}`,
    stan: `${100000 + hash * 13579 % 900000}`,
    invoiceNo: `${100000 + hash * 24680 % 900000}`,
    batchNo: `${100 + hash * 111 % 900}`,
    time: `${String(10 + hash % 12).padStart(2, '0')}:${String(hash % 60).padStart(2, '0')}:${String(hash * 2 % 60).padStart(2, '0')}`,
    last4: `${1000 + hash * 333 % 9000}`,
    address: `${10 + hash % 90} Main Boulevard, Commercial Area`
  };
};
const DISPUTES_DATA: Record<string, any> = {
  'DSP-2023-001': {
    id: 'DSP-2023-001',
    txId: 'TXN-99281',
    merchant: 'TechStore PK',
    type: 'Fraud',
    amount: 45000,
    date: '2023-10-24',
    status: 'Under Review',
    sla: '2 days',
    merchantId: 'MER-55421',
    reasonCode: '4837 - No Cardholder Authorization',
    customerClaimDate: '2023-10-22',
    slaDeadline: '2023-10-31',
    description: 'Customer claims they did not authorize this transaction for a laptop purchase.'
  },
  'DSP-2023-002': {
    id: 'DSP-2023-002',
    txId: 'TXN-99282',
    merchant: 'Fashion Hub',
    type: 'Refund',
    amount: 12500,
    date: '2023-10-23',
    status: 'Evidence Submitted',
    sla: '5 days',
    merchantId: 'MER-55422',
    reasonCode: '4853 - Cardholder Dispute',
    customerClaimDate: '2023-10-21',
    slaDeadline: '2023-11-02',
    description: 'Customer returned the clothing items but claims refund was not processed.'
  },
  'DSP-2023-003': {
    id: 'DSP-2023-003',
    txId: 'TXN-99283',
    merchant: 'Grocery Plus',
    type: 'Retrieval',
    amount: 3400,
    date: '2023-10-22',
    status: 'Merchant Notified',
    sla: '1 day',
    merchantId: 'MER-55423',
    reasonCode: '4855 - Goods Not Received',
    customerClaimDate: '2023-10-20',
    slaDeadline: '2023-10-29',
    description: 'Requesting copy of the transaction receipt and delivery confirmation.'
  },
  'DSP-2023-004': {
    id: 'DSP-2023-004',
    txId: 'TXN-99284',
    merchant: 'Electro World',
    type: 'Fraud',
    amount: 89000,
    date: '2023-10-21',
    status: 'Created',
    sla: '7 days',
    merchantId: 'MER-55424',
    reasonCode: '4837 - No Cardholder Authorization',
    customerClaimDate: '2023-10-20',
    slaDeadline: '2023-11-05',
    description: 'Cardholder states card was lost and this high-value transaction is fraudulent.'
  },
  'DSP-2023-005': {
    id: 'DSP-2023-005',
    txId: 'TXN-99285',
    merchant: 'Travel Agency',
    type: 'Refund',
    amount: 150000,
    date: '2023-10-20',
    status: 'Approved',
    sla: '-',
    merchantId: 'MER-55425',
    reasonCode: '4853 - Cardholder Dispute',
    customerClaimDate: '2023-10-18',
    slaDeadline: '-',
    description: 'Flight cancelled by airline, customer requesting full refund for tickets.'
  },
  'DSP-2023-006': {
    id: 'DSP-2023-006',
    txId: 'TXN-99286',
    merchant: 'Book Haven',
    type: 'Retrieval',
    amount: 1200,
    date: '2023-10-19',
    status: 'Rejected',
    sla: '-',
    merchantId: 'MER-55426',
    reasonCode: '4855 - Goods Not Received',
    customerClaimDate: '2023-10-17',
    slaDeadline: '-',
    description: 'Customer requested copy of receipt for expense reporting.'
  },
  'DSP-2023-007': {
    id: 'DSP-2023-007',
    txId: 'TXN-99287',
    merchant: 'TechStore PK',
    type: 'Fraud',
    amount: 32000,
    date: '2023-10-18',
    status: 'Chargeback Initiated',
    sla: '-',
    merchantId: 'MER-55421',
    reasonCode: '4837 - No Cardholder Authorization',
    customerClaimDate: '2023-10-16',
    slaDeadline: '-',
    description: 'Unrecognized transaction reported by cardholder on their statement.'
  },
  'DSP-2023-008': {
    id: 'DSP-2023-008',
    txId: 'TXN-99288',
    merchant: 'Fitness Pro',
    type: 'Refund',
    amount: 8500,
    date: '2023-10-17',
    status: 'Closed',
    sla: '-',
    merchantId: 'MER-55428',
    reasonCode: '4853 - Cardholder Dispute',
    customerClaimDate: '2023-10-15',
    slaDeadline: '-',
    description: 'Customer cancelled membership but was still charged for the current month.'
  }
};
// Enhance base data with evidence, financials, and audit logs
Object.keys(DISPUTES_DATA).forEach(key => {
  const d = DISPUTES_DATA[key];
  d.evidence = [{
    name: 'Delivery_Receipt.pdf',
    size: '1.2 MB',
    date: d.date
  }, {
    name: 'IP_Logs.csv',
    size: '45 KB',
    date: d.date
  }, {
    name: '3DS_Auth_Record.pdf',
    size: '890 KB',
    date: d.date
  }, {
    name: 'Payment_Receipt.pdf',
    size: '320 KB',
    date: d.date
  }];
  d.financials = {
    originalAmount: d.amount,
    disputedAmount: d.amount,
    fee: Math.round(d.amount * 0.03),
    liability: ['Approved', 'Closed'].includes(d.status) ? 'Resolved' : 'Pending'
  };
  d.auditLog = [{
    date: `${d.date} 10:00`,
    action: 'Dispute Created',
    actor: 'System'
  }, {
    date: `${d.date} 10:15`,
    action: 'Merchant Notified',
    actor: 'System'
  }];
  if (['Evidence Submitted', 'Under Review', 'Approved', 'Rejected', 'Chargeback Initiated', 'Closed'].includes(d.status)) {
    d.auditLog.unshift({
      date: `${d.date} 14:30`,
      action: 'Evidence Submitted',
      actor: `Merchant (${d.merchant})`
    });
  }
  if (['Under Review', 'Approved', 'Rejected', 'Chargeback Initiated', 'Closed'].includes(d.status)) {
    d.auditLog.unshift({
      date: `${d.date} 16:45`,
      action: 'Review Started',
      actor: 'Dispute Officer'
    });
  }
  if (['Approved', 'Rejected', 'Chargeback Initiated', 'Closed'].includes(d.status)) {
    d.auditLog.unshift({
      date: `${d.date} 18:00`,
      action: `Status changed to ${d.status}`,
      actor: 'Dispute Officer'
    });
  }
});
const DisputeDetails: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'keep_open' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState<any>(null);
  const dispute = DISPUTES_DATA[id || ''] || DISPUTES_DATA['DSP-2023-001'];
  const randomData = generateRandomData(dispute.id);
  const handleActionSubmit = () => {
    console.log(`Submitting action: ${actionType} with notes: ${actionNotes}`);
    setIsActionModalOpen(false);
    setActionNotes('');
  };
  const openActionModal = (type: 'approve' | 'reject' | 'keep_open') => {
    setActionType(type);
    setIsActionModalOpen(true);
  };
  const openPdfViewer = (file: any) => {
    setSelectedEvidence(file);
    setIsPdfViewerOpen(true);
  };
  const renderPdfContent = () => {
    if (!selectedEvidence) return null;
    if (selectedEvidence.name === 'Payment_Receipt.pdf') {
      return <div className="bg-gray-100 p-8 flex justify-center items-center min-h-[600px] overflow-hidden relative" data-id="element-3785">
          <div className="bg-[#fefdf8] shadow-lg p-6 max-w-[320px] w-full transform rotate-[0.3deg] font-mono text-sm text-gray-800 relative overflow-hidden" data-id="element-3786">
            {/* Faint COPY watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5" data-id="element-3787">
              <span className="text-6xl font-bold transform -rotate-45 tracking-widest text-gray-900" data-id="element-3788">
                COPY
              </span>
            </div>

            <div className="text-center whitespace-pre-wrap leading-tight" data-id="element-3789">
              {`================================\n`}
              <span className="font-bold text-base" data-id="element-3790">
                {dispute.merchant.toUpperCase()}
              </span>
              {`\n`}
              {`${randomData.address}\n`}
              {`${randomData.city}, Pakistan\n`}
              {`================================\n`}
            </div>

            <div className="mt-4 mb-4 whitespace-pre-wrap leading-tight" data-id="element-3791">
              {`Terminal ID: ${randomData.terminalId}\n`}
              {`Merchant ID: ${dispute.merchantId}\n`}
              {`Date: ${dispute.date}  Time: ${randomData.time}\n`}
            </div>

            <div className="text-center whitespace-pre-wrap leading-tight" data-id="element-3792">
              {`--------------------------------\n`}
              <span className="font-bold" data-id="element-3793">CARD TRANSACTION RECEIPT</span>
              {`\n`}
              <span className="font-bold" data-id="element-3794">SALE / PURCHASE</span>
              {`\n`}
              {`--------------------------------\n`}
            </div>

            <div className="mt-4 mb-4 whitespace-pre-wrap leading-tight flex flex-col space-y-1" data-id="element-3795">
              <div className="flex justify-between" data-id="element-3796">
                <span data-id="element-3797">Card Type:</span>
                <span data-id="element-3798">{randomData.cardType}</span>
              </div>
              <div className="flex justify-between" data-id="element-3799">
                <span data-id="element-3800">Card No:</span>
                <span data-id="element-3801">
                  {randomData.cardPrefix}XXX XXXX XXXX {randomData.last4}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3802">
                <span data-id="element-3803">Entry Mode:</span>
                <span data-id="element-3804">CHIP</span>
              </div>
              <div className="flex justify-between" data-id="element-3805">
                <span data-id="element-3806">Auth Code:</span>
                <span data-id="element-3807">{randomData.authCode}</span>
              </div>
              <div className="flex justify-between" data-id="element-3808">
                <span data-id="element-3809">RRN:</span>
                <span data-id="element-3810">{randomData.rrn}</span>
              </div>
              <div className="flex justify-between" data-id="element-3811">
                <span data-id="element-3812">STAN:</span>
                <span data-id="element-3813">{randomData.stan}</span>
              </div>
              <div className="flex justify-between" data-id="element-3814">
                <span data-id="element-3815">Invoice No:</span>
                <span data-id="element-3816">{randomData.invoiceNo}</span>
              </div>
              <div className="flex justify-between" data-id="element-3817">
                <span data-id="element-3818">Batch No:</span>
                <span data-id="element-3819">{randomData.batchNo}</span>
              </div>
            </div>

            <div className="text-center whitespace-pre-wrap leading-tight" data-id="element-3820">
              {`--------------------------------\n`}
              <div className="flex justify-between font-bold text-base" data-id="element-3821">
                <span data-id="element-3822">AMOUNT:</span>
                <span data-id="element-3823">PKR {dispute.amount.toLocaleString()}</span>
              </div>
              {`--------------------------------\n`}
            </div>

            <div className="mt-6 text-center whitespace-pre-wrap leading-tight" data-id="element-3824">
              <span className="font-bold" data-id="element-3825">** APPROVED **</span>
              {`\n\n`}
              {`CARDHOLDER VERIFIED BY PIN\n\n`}
              {`Please retain this receipt\n`}
              {`for your records.\n`}
              {`================================\n`}
              <span className="font-bold" data-id="element-3826">CUSTOMER COPY</span>
              {`\n`}
              {`================================`}
            </div>
          </div>
        </div>;
    }
    if (selectedEvidence.name === 'Delivery_Receipt.pdf') {
      return <div className="p-8 border border-gray-200 rounded-lg shadow-sm bg-white text-gray-800 relative overflow-hidden min-h-[600px]" data-id="element-3827">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5" data-id="element-3828">
            <span className="text-8xl font-bold transform -rotate-45 tracking-widest" data-id="element-3829">
              CONFIDENTIAL
            </span>
          </div>
          <div className="flex justify-between items-start border-b-2 border-gray-800 pb-6 mb-6" data-id="element-3830">
            <div data-id="element-3831">
              <h2 className="text-3xl font-bold uppercase tracking-wider" data-id="element-3832">
                {dispute.merchant}
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3833">
                Merchant ID: {dispute.merchantId}
              </p>
            </div>
            <div className="text-right" data-id="element-3834">
              <h1 className="text-2xl font-bold text-gray-400 uppercase" data-id="element-3835">
                Delivery Receipt
              </h1>
              <p className="text-sm font-medium mt-2" data-id="element-3836">Date: {dispute.date}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-3837">
            <div data-id="element-3838">
              <h3 className="text-sm font-bold text-gray-500 uppercase mb-2" data-id="element-3839">
                Order Details
              </h3>
              <p className="font-medium" data-id="element-3840">
                Transaction ID:{' '}
                <span className="font-normal" data-id="element-3841">{dispute.txId}</span>
              </p>
              <p className="font-medium" data-id="element-3842">
                Amount:{' '}
                <span className="font-normal" data-id="element-3843">
                  PKR {dispute.amount.toLocaleString()}
                </span>
              </p>
              <p className="font-medium" data-id="element-3844">
                Item:{' '}
                <span className="font-normal" data-id="element-3845">General Merchandise (1x)</span>
              </p>
            </div>
            <div data-id="element-3846">
              <h3 className="text-sm font-bold text-gray-500 uppercase mb-2" data-id="element-3847">
                Delivery Details
              </h3>
              <p className="font-medium" data-id="element-3848">
                Courier: <span className="font-normal" data-id="element-3849">TCS Express</span>
              </p>
              <p className="font-medium" data-id="element-3850">
                Tracking #:{' '}
                <span className="font-normal" data-id="element-3851">
                  TCS-{Math.floor(10000000 + Math.random() * 90000000)}
                </span>
              </p>
              <p className="font-medium" data-id="element-3852">
                Delivered To:{' '}
                <span className="font-normal" data-id="element-3853">
                  Customer, {randomData.address}, {randomData.city}
                </span>
              </p>
            </div>
          </div>

          <div className="mt-16 border-t border-gray-300 pt-8" data-id="element-3854">
            <div className="flex justify-between items-end" data-id="element-3855">
              <div className="w-64" data-id="element-3856">
                <div className="border-b border-gray-800 h-10 mb-2 flex items-end justify-center" data-id="element-3857">
                  <span className="font-cursive text-xl text-blue-800" data-id="element-3858">
                    Signed
                  </span>
                </div>
                <p className="text-xs text-center text-gray-500" data-id="element-3859">
                  Customer Signature
                </p>
              </div>
              <div className="text-right" data-id="element-3860">
                <div className="inline-block border-2 border-green-600 text-green-600 font-bold px-4 py-2 rounded transform -rotate-12" data-id="element-3861">
                  DELIVERED
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-400" data-id="element-3862">
            Generated by Paysys MMS - Document Reference: REF-
            {Math.floor(Math.random() * 1000000)}
          </div>
        </div>;
    }
    if (selectedEvidence.name === 'IP_Logs.csv') {
      return <div className="p-8 border border-gray-200 rounded-lg shadow-sm bg-white text-gray-800 relative overflow-hidden min-h-[600px]" data-id="element-3863">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5" data-id="element-3864">
            <span className="text-8xl font-bold transform -rotate-45 tracking-widest" data-id="element-3865">
              CONFIDENTIAL
            </span>
          </div>
          <h2 className="text-xl font-bold mb-6 border-b pb-2" data-id="element-3866">
            System IP Access Logs - {dispute.txId}
          </h2>
          <table className="w-full text-sm text-left" data-id="element-3867">
            <thead className="bg-gray-100" data-id="element-3868">
              <tr data-id="element-3869">
                <th className="px-4 py-2" data-id="element-3870">Timestamp</th>
                <th className="px-4 py-2" data-id="element-3871">IP Address</th>
                <th className="px-4 py-2" data-id="element-3872">Location</th>
                <th className="px-4 py-2" data-id="element-3873">User Agent</th>
              </tr>
            </thead>
            <tbody data-id="element-3874">
              <tr className="border-b" data-id="element-3875">
                <td className="px-4 py-3" data-id="element-3876">
                  {dispute.date} {randomData.time}
                </td>
                <td className="px-4 py-3 font-mono" data-id="element-3877">119.160.116.42</td>
                <td className="px-4 py-3" data-id="element-3878">{randomData.city}, PK</td>
                <td className="px-4 py-3 text-xs text-gray-500" data-id="element-3879">
                  Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
                </td>
              </tr>
              <tr className="border-b bg-gray-50" data-id="element-3880">
                <td className="px-4 py-3" data-id="element-3881">{dispute.date} 09:15:22</td>
                <td className="px-4 py-3 font-mono" data-id="element-3882">119.160.116.42</td>
                <td className="px-4 py-3" data-id="element-3883">{randomData.city}, PK</td>
                <td className="px-4 py-3 text-xs text-gray-500" data-id="element-3884">
                  Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
                </td>
              </tr>
              <tr className="border-b" data-id="element-3885">
                <td className="px-4 py-3" data-id="element-3886">{dispute.date} 09:18:01</td>
                <td className="px-4 py-3 font-mono" data-id="element-3887">119.160.116.42</td>
                <td className="px-4 py-3" data-id="element-3888">{randomData.city}, PK</td>
                <td className="px-4 py-3 text-xs text-gray-500" data-id="element-3889">
                  Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
                </td>
              </tr>
            </tbody>
          </table>
          <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-400" data-id="element-3890">
            Generated by Paysys MMS - Document Reference: REF-
            {Math.floor(Math.random() * 1000000)}
          </div>
        </div>;
    }
    if (selectedEvidence.name === '3DS_Auth_Record.pdf') {
      return <div className="p-8 border border-gray-200 rounded-lg shadow-sm bg-white text-gray-800 relative overflow-hidden min-h-[600px]" data-id="element-3891">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5" data-id="element-3892">
            <span className="text-8xl font-bold transform -rotate-45 tracking-widest" data-id="element-3893">
              CONFIDENTIAL
            </span>
          </div>
          <div className="text-center mb-8 border-b pb-6" data-id="element-3894">
            <h2 className="text-2xl font-bold tracking-tight" data-id="element-3895">
              3D Secure Authentication Record
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-3896">
              Verified by {randomData.cardType} Identity Check
            </p>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg mb-6 border border-gray-200" data-id="element-3897">
            <div className="grid grid-cols-2 gap-y-4 text-sm" data-id="element-3898">
              <div className="font-medium text-gray-500" data-id="element-3899">Transaction ID:</div>
              <div className="font-mono" data-id="element-3900">{dispute.txId}</div>

              <div className="font-medium text-gray-500" data-id="element-3901">Merchant:</div>
              <div data-id="element-3902">
                {dispute.merchant} ({dispute.merchantId})
              </div>

              <div className="font-medium text-gray-500" data-id="element-3903">Card Number:</div>
              <div className="font-mono" data-id="element-3904">
                {randomData.cardPrefix}XXX XXXX XXXX {randomData.last4}
              </div>

              <div className="font-medium text-gray-500" data-id="element-3905">Amount:</div>
              <div data-id="element-3906">PKR {dispute.amount.toLocaleString()}</div>

              <div className="font-medium text-gray-500" data-id="element-3907">Date/Time:</div>
              <div data-id="element-3908">
                {dispute.date} {randomData.time} UTC
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-6 rounded-lg border border-green-200" data-id="element-3909">
            <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center" data-id="element-3910">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-3911" /> Authentication
              Successful
            </h3>
            <div className="grid grid-cols-2 gap-y-4 text-sm" data-id="element-3912">
              <div className="font-medium text-gray-500" data-id="element-3913">ECI Indicator:</div>
              <div className="font-mono font-bold" data-id="element-3914">
                05 (Fully Authenticated)
              </div>

              <div className="font-medium text-gray-500" data-id="element-3915">CAVV Value:</div>
              <div className="font-mono text-xs break-all" data-id="element-3916">
                AAABBCCCDDDEEEFFFGGGHHHIIIJJJ=
              </div>

              <div className="font-medium text-gray-500" data-id="element-3917">XID:</div>
              <div className="font-mono text-xs break-all" data-id="element-3918">
                1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZ
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-400" data-id="element-3919">
            Generated by Paysys MMS - Document Reference: REF-
            {Math.floor(Math.random() * 1000000)}
          </div>
        </div>;
    }
    return <div className="p-8 text-center text-gray-500 min-h-[400px] flex flex-col items-center justify-center" data-id="element-3920">
        <FileTextIcon className="h-16 w-16 mb-4 text-gray-300" data-id="element-3921" />
        <p data-id="element-3922">Document preview not available for this file type.</p>
      </div>;
  };
  return <div className="space-y-6 pb-12" data-id="element-3923">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3924">
        <div className="flex items-center space-x-4" data-id="element-3925">
          <button onClick={() => navigate('/disputes')} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors" data-id="element-3926">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-3927" />
          </button>
          <div data-id="element-3928">
            <div className="flex items-center space-x-3" data-id="element-3929">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3930">
                Dispute {dispute.id}
              </h1>
              <StatusBadge status={['Approved', 'Closed'].includes(dispute.status) ? 'success' : ['Under Review', 'Chargeback Initiated'].includes(dispute.status) ? 'warning' : ['Rejected'].includes(dispute.status) ? 'danger' : 'info'} text={dispute.status} data-id="element-3931" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-3932">
              Transaction {dispute.txId} • {dispute.merchant}
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-3933">
          <button onClick={() => openActionModal('keep_open')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3934">
            Keep Open
          </button>
          <button onClick={() => openActionModal('reject')} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-3935">
            Reject Dispute
          </button>
          <button onClick={() => openActionModal('approve')} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-3936">
            Approve Dispute
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-3937">
        <nav className="-mb-px flex space-x-8" data-id="element-3938">
          {['overview', 'evidence', 'financials', 'audit'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`${activeTab === tab ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`} data-id="element-3939">
              {tab === 'audit' ? 'Audit Log' : tab}
            </button>)}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6" data-id="element-3940">
        {activeTab === 'overview' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3941">
            <div className="md:col-span-2 space-y-6" data-id="element-3942">
              <Card title="Dispute Information" data-id="element-3943">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6" data-id="element-3944">
                  <div data-id="element-3945">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3946">
                      Dispute Type
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold" data-id="element-3947">
                      {dispute.type}
                    </dd>
                  </div>
                  <div data-id="element-3948">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3949">
                      Reason Code
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-3950">
                      {dispute.reasonCode}
                    </dd>
                  </div>
                  <div data-id="element-3951">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3952">
                      Amount Disputed
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold text-red-600 dark:text-red-400" data-id="element-3953">
                      PKR {dispute.amount.toLocaleString()}
                    </dd>
                  </div>
                  <div data-id="element-3954">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3955">
                      Customer Claim Date
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-3956">
                      {dispute.customerClaimDate}
                    </dd>
                  </div>
                  <div className="sm:col-span-2" data-id="element-3957">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3958">
                      Description / Remarks
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-3 rounded-md border border-gray-100 dark:border-gray-700" data-id="element-3959">
                      {dispute.description}
                    </dd>
                  </div>
                </dl>
              </Card>

              <Card title="Merchant Details" data-id="element-3960">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6" data-id="element-3961">
                  <div data-id="element-3962">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3963">
                      Merchant Name
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-3964">
                      {dispute.merchant}
                    </dd>
                  </div>
                  <div data-id="element-3965">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3966">
                      Merchant ID
                    </dt>
                    <dd className="mt-1 text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" data-id="element-3967">
                      {dispute.merchantId}
                    </dd>
                  </div>
                </dl>
              </Card>
            </div>
            <div className="space-y-6" data-id="element-3968">
              <Card title="SLA & Timelines" className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800" data-id="element-3969">
                <div className="space-y-4" data-id="element-3970">
                  <div className="flex items-start" data-id="element-3971">
                    <ClockIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3" data-id="element-3972" />
                    <div data-id="element-3973">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3974">
                        SLA Deadline
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3975">
                        {dispute.slaDeadline}
                      </p>
                    </div>
                  </div>
                  {dispute.slaDeadline !== '-' && <div className="flex items-start" data-id="element-3976">
                      <AlertCircleIcon className="h-5 w-5 text-orange-500 mt-0.5 mr-3" data-id="element-3977" />
                      <div data-id="element-3978">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3979">
                          Time Remaining
                        </p>
                        <p className="text-sm text-orange-600 font-semibold" data-id="element-3980">
                          {dispute.sla}
                        </p>
                      </div>
                    </div>}
                </div>
              </Card>
            </div>
          </div>}

        {activeTab === 'evidence' && <Card title="Merchant Evidence" data-id="element-3981">
            {dispute.evidence.length > 0 ? <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3982">
                {dispute.evidence.map((file: any, index: number) => <li key={index} className="py-4 flex items-center justify-between" data-id="element-3983">
                    <div className="flex items-center" data-id="element-3984">
                      <FileTextIcon className="h-8 w-8 text-gray-400 mr-4" data-id="element-3985" />
                      <div data-id="element-3986">
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3987">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-3988">
                          Uploaded on {file.date} • {file.size}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-3989">
                      <button onClick={() => openPdfViewer(file)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors" title="View Document" data-id="element-3990">
                        <EyeIcon className="h-5 w-5" data-id="element-3991" />
                      </button>
                      <button className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors" title="Download" data-id="element-3992">
                        <DownloadIcon className="h-5 w-5" data-id="element-3993" />
                      </button>
                    </div>
                  </li>)}
              </ul> : <div className="text-center py-8" data-id="element-3994">
                <FileTextIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" data-id="element-3995" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3996">
                  No evidence submitted
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-3997">
                  The merchant has not uploaded any supporting documents yet.
                </p>
              </div>}
          </Card>}

        {activeTab === 'financials' && <Card title="Financial Impact" data-id="element-3998">
            <div className="overflow-hidden bg-white dark:bg-gray-800 shadow sm:rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3999">
              <div className="px-4 py-5 sm:p-0" data-id="element-4000">
                <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-700" data-id="element-4001">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-4002">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4003">
                      Original Transaction Amount
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" data-id="element-4004">
                      PKR {dispute.financials.originalAmount.toLocaleString()}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50" data-id="element-4005">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4006">
                      Disputed Amount
                    </dt>
                    <dd className="mt-1 text-sm text-red-600 dark:text-red-400 font-semibold sm:mt-0 sm:col-span-2" data-id="element-4007">
                      PKR {dispute.financials.disputedAmount.toLocaleString()}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-4008">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4009">
                      Chargeback Fee (if applicable)
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" data-id="element-4010">
                      PKR {dispute.financials.fee.toLocaleString()}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50 dark:bg-gray-900/50" data-id="element-4011">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4012">
                      Current Liability
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2" data-id="element-4013">
                      <StatusBadge status={dispute.financials.liability === 'Resolved' ? 'success' : 'warning'} text={dispute.financials.liability} data-id="element-4014" />
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </Card>}

        {activeTab === 'audit' && <Card title="Dispute Lifecycle Audit" data-id="element-4015">
            <div className="flow-root" data-id="element-4016">
              <ul className="-mb-8" data-id="element-4017">
                {dispute.auditLog.map((log: any, index: number) => <li key={index} data-id="element-4018">
                    <div className="relative pb-8" data-id="element-4019">
                      {index !== dispute.auditLog.length - 1 ? <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" data-id="element-4020"></span> : null}
                      <div className="relative flex space-x-3" data-id="element-4021">
                        <div data-id="element-4022">
                          <span className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center ring-8 ring-white dark:ring-gray-900" data-id="element-4023">
                            <ClockIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" data-id="element-4024" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4" data-id="element-4025">
                          <div data-id="element-4026">
                            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4027">
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-4028">
                                {log.action}
                              </span>{' '}
                              by {log.actor}
                            </p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-400" data-id="element-4029">
                            {log.date}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>)}
              </ul>
            </div>
          </Card>}
      </div>

      {/* Action Modal */}
      <Modal isOpen={isActionModalOpen} onClose={() => setIsActionModalOpen(false)} title={actionType === 'approve' ? 'Approve Dispute (Merchant Debited)' : actionType === 'reject' ? 'Reject Dispute (Merchant Evidence Accepted)' : 'Keep Dispute Open'} data-id="element-4030">
        <div className="space-y-4" data-id="element-4031">
          <div className={`p-4 rounded-md ${actionType === 'approve' ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300' : actionType === 'reject' ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'}`} data-id="element-4032">
            <div className="flex" data-id="element-4033">
              <div className="flex-shrink-0" data-id="element-4034">
                {actionType === 'approve' ? <CheckCircleIcon className="h-5 w-5" data-id="element-4035" /> : actionType === 'reject' ? <XCircleIcon className="h-5 w-5" data-id="element-4036" /> : <AlertCircleIcon className="h-5 w-5" data-id="element-4037" />}
              </div>
              <div className="ml-3" data-id="element-4038">
                <h3 className="text-sm font-medium" data-id="element-4039">
                  {actionType === 'approve' ? 'This action will initiate a refund to the customer and debit the merchant.' : actionType === 'reject' ? 'This action will close the dispute with no financial impact to the merchant.' : 'This action will keep the dispute open for further investigation or pending scheme decision.'}
                </h3>
              </div>
            </div>
          </div>

          <div data-id="element-4040">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4041">
              Officer Notes / Remarks (Required)
            </label>
            <textarea id="notes" rows={4} className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="Enter justification for this decision..." value={actionNotes} onChange={e => setActionNotes(e.target.value)} data-id="element-4042" />
          </div>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" data-id="element-4043">
            <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:col-start-2 sm:text-sm ${actionType === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : actionType === 'reject' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`} onClick={handleActionSubmit} disabled={!actionNotes.trim()} data-id="element-4044">
              Confirm Action
            </button>
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm" onClick={() => setIsActionModalOpen(false)} data-id="element-4045">
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal isOpen={isPdfViewerOpen} onClose={() => setIsPdfViewerOpen(false)} title={`Document Viewer - ${selectedEvidence?.name || ''}`} size="xl" data-id="element-4046">
        <div className="bg-gray-100 p-4 rounded-md overflow-auto max-h-[70vh]" data-id="element-4047">
          {renderPdfContent()}
        </div>
        <div className="mt-4 flex justify-end" data-id="element-4048">
          <button onClick={() => setIsPdfViewerOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4049">
            Close Viewer
          </button>
        </div>
      </Modal>
    </div>;
};
export default DisputeDetails;